import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock } from "lucide-react";
import { api } from "@/lib/api";
import CourseModal from "@/components/site/CourseModal";
import { scrollToSection } from "@/lib/scroll";

const goContact = (course, branch) => {
  scrollToSection("contacto");
  window.dispatchEvent(
    new CustomEvent("ie:preselect", {
      detail: {
        course: course?.title || course || "",
        branch: branch?.name || "",
        },
      })
    );
};

export default function Courses() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/courses").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  return (
    <section id="cursos" className="bg-white pt-8 pb-16 md:pt-10 md:pb-24 lg:pt-12 lg:pb-24" data-testid="courses-section">
      <div className="container-ie">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-8 md:mb-14">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="dot-red" />
              <span className="overline">Programas Profesionales</span>
            </div>
            <h2 className="font-display font-semibold tracking-tighter text-4xl md:text-5xl lg:text-6xl text-[#0a0a0a]">
              Cursos diseñados para
              <br /> <span className="italic font-light">profesionalizar</span> tu talento.
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-base md:text-lg font-light text-[#525252] leading-relaxed">
              Cuatro programas de alto nivel, impartidos por profesionales en
              activo, con prácticas reales en nuestras instalaciones premium.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-7">
          {items.map((c, i) => (
            <motion.article
              key={c.id}
              initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
              whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
              className="course-card cursor-pointer"
              data-testid={`course-card-${c.id}`}
              onClick={() => setSelected(c)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSelected(c);
                }
              }}
              tabIndex={0}
              role="button"
              aria-label={`Ver detalles del curso ${c.title}`}
            >
              <div className="img-wrap">
                <img src={c.image} alt={c.title} />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-[0.65rem] uppercase tracking-[0.22em] text-[#c40000] font-semibold">
                    {c.tagline}
                  </span>
                  {(() => {
                    const nums = c.prices
                      ? Object.values(c.prices).filter((v) => typeof v === "number")
                      : [];
                    if (nums.length === 0) return null;
                    return (
                      <span
                        data-testid={`course-card-price-${c.id}`}
                        className="shrink-0 text-[0.65rem] font-semibold tracking-tight bg-[#0a0a0a] text-white px-2 py-1 whitespace-nowrap"
                      >
                        Desde ${Math.min(...nums)}/sem
                      </span>
                    );
                  })()}
                </div>
                <h3 className="mt-2 font-display font-semibold text-xl md:text-[1.35rem] tracking-tight text-[#0a0a0a]">
                  {c.title}
                </h3>
                <p className="mt-3 text-sm font-light text-[#525252] leading-relaxed">
                  {c.short_description || c.description}
                </p>

                <div className="mt-5 flex flex-col gap-2 text-xs text-[#525252]">
                  <span className="flex items-center gap-2">
                    <Clock size={14} strokeWidth={1.5} />
                    <span>{c.duration}</span>
                  </span>
                </div>

                <button
                  data-testid={`course-cta-${c.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelected(c);
                  }}
                  className="mt-6 group inline-flex items-center justify-between border-t border-black/10 pt-5 text-xs font-semibold uppercase tracking-[0.18em] text-[#0a0a0a] hover:text-[#c40000] transition-colors"
                >
                  Más información
                  <ArrowUpRight
                    size={16}
                    strokeWidth={1.6}
                    className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </button>
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      <CourseModal
        course={selected}
        onClose={() => setSelected(null)}
        onEnroll={(c, b) => goContact(c, b)}
      />
    </section>
  );
}
